import React, { useCallback, useEffect, useMemo, useReducer } from "react";

import { ZoneDimensions, ZoneWithCells } from "../../types";
import { Background } from "./Background";
import { Controls } from "./Controls";
import { InputHandler } from "./InputHandler";
import { RowsAndCols } from "./RowsAndCols";
import { reducer, useDefaultState } from "./state";
import { useDimension } from "./useDimensions";
import { useGutters } from "./useGutters";
import "./index.scss";
import { CellTypeList } from "./CellTypeList";
import { Selection } from "../Selection";

type Props = Readonly<{
  containerHeight: number;
  containerWidth: number;
  onCancel: () => void;
  onSubmit: () => void;
  onUpdateCells: (cells: ReadonlyArray<ZoneDimensions>) => void;
  pageImage: HTMLImageElement;
  review: boolean;
  zone: ZoneWithCells;
  zoom: number;
}>;

const GUTTER = 30; // leave a gutter around the background image of 30 px
const CONTROLS_HEIGHT = 60; // the height of the controls menu

export const TableCellZoning = ({
  containerHeight,
  containerWidth,
  onCancel,
  onSubmit,
  onUpdateCells,
  pageImage,
  review,
  zone,
  zoom,
}: Props) => {
  const [scale, xOffset, yOffset] = useDimension(
    containerWidth,
    containerHeight - CONTROLS_HEIGHT,
    zone,
    GUTTER,
  );
  const defaultState = useDefaultState(zone);
  const [state, dispatch] = useReducer(reducer, defaultState);
  const gutters = useGutters(
    GUTTER,
    containerWidth,
    containerHeight - CONTROLS_HEIGHT,
    xOffset,
    yOffset,
  );
  const handoffDimensions = useMemo(
    () => ({
      gutters,
      height: containerHeight - CONTROLS_HEIGHT,
      scale,
      width: containerWidth,
      xOffset,
      yOffset,
      zoneHeight: zone.maxY - zone.minY,
      zoneWidth: zone.maxX - zone.minX,
    }),
    [
      containerHeight,
      containerWidth,
      scale,
      xOffset,
      yOffset,
      zone.minX,
      zone.minY,
      zone.maxX,
      zone.maxY,
      zoom,
    ],
  );
  const onReset = useCallback(() => {
    dispatch({
      type: "RESET",
    });
  }, [dispatch]);
  const onClear = useCallback(() => {
    dispatch({
      type: "CLEAR CELLS",
    });
  }, [dispatch]);
  useEffect(() => {
    if (state === defaultState) {
      return;
    }

    if (Array.isArray(zone.cells) && state.cells.length === zone.cells.length) {
      for (let i = 0; i < state.cells.length; i++) {
        
        const newZone = state.cells[i];
        const oldZone = zone.cells[i];

        if (
          newZone.minX + zone.minX !== oldZone.minX ||
          newZone.minY + zone.minY !== oldZone.minY ||
          newZone.maxX + zone.minX !== oldZone.maxX ||
          newZone.maxY + zone.minY !== oldZone.maxY ||
          newZone.cellType !== oldZone.cellType ||
          newZone.bottomBorderImplicit !== oldZone.bottomBorderImplicit ||
          newZone.topBorderImplicit !== oldZone.topBorderImplicit ||
          newZone.leftBorderImplicit !== oldZone.leftBorderImplicit ||
          newZone.rightBorderImplicit !== oldZone.rightBorderImplicit
        ) {
          onUpdateCells(
            state.cells.map(c => ({
              maxX: c.maxX + zone.minX,
              maxY: c.maxY + zone.minY,
              minX: c.minX + zone.minX,
              minY: c.minY + zone.minY,
              cellType: c.cellType,
              bottomBorderImplicit: c.bottomBorderImplicit,
              topBorderImplicit: c.topBorderImplicit,
              leftBorderImplicit: c.leftBorderImplicit,
              rightBorderImplicit: c.rightBorderImplicit,
            })),
          );
          return;
        }
      }
      return;
    }
    onUpdateCells(
      state.cells.map(c => ({
        maxX: c.maxX + zone.minX,
        maxY: c.maxY + zone.minY,
        minX: c.minX + zone.minX,
        minY: c.minY + zone.minY,
        cellType: c.cellType,
        bottomBorderImplicit: c.bottomBorderImplicit,
        topBorderImplicit: c.topBorderImplicit,
        leftBorderImplicit: c.leftBorderImplicit,
        rightBorderImplicit: c.rightBorderImplicit,
      })),
    );
  }, [onUpdateCells, state.cells, zone.cells]);
  return (
    <div className="table-zoning">
      <div className="fake-left-menu">
        <CellTypeList
          dispatch={dispatch}
          readonly={review}
        />
      </div>
      <div className="table-zoning">
        <InputHandler
          dispatch={dispatch}
          review={review}
          state={state}
          zoom={zoom}
          {...handoffDimensions}
        >
          <Background pageImage={pageImage} zone={zone} zoom={zoom} showTableUI = {state.showTableUI} {...handoffDimensions} />
          <RowsAndCols state={state} zoom={zoom} {...handoffDimensions} />
        </InputHandler>
        <Controls
          disabled={review}
          onCancel={onCancel}
          onClear={onClear}
          onReset={onReset}
          onSubmit={onSubmit}
        />
      </div>
    </div>
  );
};
